import liff from "@line/liff";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";
import type { AuthUser } from "../types";

const LIFF_ID = import.meta.env.VITE_LIFF_ID as string;

type AuthStatus = "loading" | "authed" | "unauthed";

type AuthState = {
  status: AuthStatus;
  user: AuthUser | null;
  login: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // LIFF must be initialised before login()/logout()/getIDToken() can be used.
      try {
        await liff.init({ liffId: LIFF_ID });
      } catch (error) {
        console.error("[Auth] LIFF init failed", error);
      }

      // 1. Already have a server session? (cookie)
      // getMe() resolves to null for an anonymous visitor — only a real user
      // short-circuits the LINE token exchange below.
      try {
        const me = await api.getMe();
        if (me) {
          if (!cancelled) {
            setUser(me);
            setStatus("authed");
          }
          return;
        }
      } catch {
        /* no session yet */
      }

      // 2. Logged into LINE but no server session → exchange the ID token.
      try {
        if (liff.isLoggedIn()) {
          const idToken = liff.getIDToken();
          if (idToken) {
            const { user: exchanged } = await api.authLine(idToken);
            if (!cancelled) {
              setUser(exchanged);
              setStatus("authed");
            }
            return;
          }
        }
      } catch (error) {
        console.error("[Auth] Token exchange failed", error);
      }

      if (!cancelled) setStatus("unauthed");
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = () => {
    // Redirects to the LINE login flow; on return the effect above exchanges the token.
    liff.login();
  };

  const logout = async () => {
    await api.logout().catch(() => {});
    try {
      if (liff.isLoggedIn()) liff.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    setStatus("unauthed");
  };

  return (
    <AuthContext.Provider value={{ status, user, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
